import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import AddCaseLogModal from "./AddCaseLogModal";
import { Form, Button, Dropdown, Modal, Table, Container, Row, Col, Card, Pagination, Accordion, FloatingLabel, Spinner } from 'react-bootstrap';
import axios from "axios";
import { API_ENDPOINTS } from './config/api';
import ProgramPGYUtils from './utils/programPGYUtils';
import PageTitle from './components/PageTitle';

const ResidentCaseLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    rotation: '',
    attending: '',
    patientPopulation: '',
    role: '',
    pgyLevel: '',
    robotic: '',
    minimallyInvasive: ''
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('current');
  const [rotations, setRotations] = useState([]);
  const [attendings, setAttendings] = useState([]);
  const [rotationSchedules, setRotationSchedules] = useState({});
  const [residentId, setResidentId] = useState(null);
  const [residentPGY, setResidentPGY] = useState(null);
  const [residentName, setResidentName] = useState('');
  const [attendingUsers, setAttendingUsers] = useState([]);
  const [exportFilters, setExportFilters] = useState({
    startDate: '',
    endDate: '',
    pgyLevel: '',
    rotation: '',
    attending: '',
    reportType: 'detailed'
  });
  const [rotationSchedulesLoaded, setRotationSchedulesLoaded] = useState(false);
  const [residentDataLoaded, setResidentDataLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // PGY level configuration
  const [pgyLevelOptions, setPgyLevelOptions] = useState([]);
  const [maxPGYLevel, setMaxPGYLevel] = useState(5);
  const [programDetails, setProgramDetails] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  // Fetch program details and PGY levels
  const fetchProgramDetails = async () => {
    setProgramLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('Program details received:', data);
        setProgramDetails(data);
        
        // Fetch PGY levels for this program
        if (data.id) {
          console.log('Fetching PGY levels for programId:', data.id);
          await fetchPGYLevels(data.id);
        } else {
          console.log('No programId found in program details');
        }
      }
    } catch (err) {
      console.error("Failed to fetch program details", err);
    } finally {
      setProgramLoading(false);
    }
  };

  // Fetch PGY level options for the program
  const fetchPGYLevels = async (programId) => {
    try {
      console.log('Fetching PGY levels for program:', programId);
      const options = await ProgramPGYUtils.getPGYLevelOptions(programId);
      console.log('Received PGY options:', options);
      setPgyLevelOptions(options);
      setMaxPGYLevel(options.length > 0 ? options[options.length - 1].value : 5);
    } catch (error) {
      console.error('Error fetching PGY levels:', error);
      // Fallback to default 5 levels
      const fallbackOptions = Array.from({ length: 5 }, (_, i) => ({
        value: i + 1,
        label: `PGY-${i + 1}`
      }));
      console.log('Using fallback PGY options:', fallbackOptions);
      setPgyLevelOptions(fallbackOptions);
      setMaxPGYLevel(5);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchCaseLogs();
        await fetchProgramDetails(); // Fetch program details and PGY levels
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []);

  useEffect(() => {
    // Update rotations and attendings when logs change
    if (logs.length > 0) {
      const uniqueRotations = [...new Set(logs.map(log => log.rotation))].filter(Boolean).sort();
      const uniqueAttendings = [...new Set(logs.map(log => log.attending))].filter(Boolean).sort();
      setRotations(uniqueRotations);
      setAttendings(uniqueAttendings);
    }
  }, [logs]);

  useEffect(() => {
    console.log('ResidentCaseLogPage useEffect running...');
    
    // Fetch rotation schedules
    const fetchRotationSchedules = async () => {
      console.log('fetchRotationSchedules called');
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios.get(`${API_ENDPOINTS.SCHEDULES}/rotations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Fetched rotation schedules:', res.data);
        setRotationSchedules(res.data);
        setRotationSchedulesLoaded(true);
      } catch (err) {
        console.error('Error fetching rotation schedules:', err);
      }
    };
    // Fetch resident info
    const fetchResidentInfo = async () => {
      console.log('Starting fetchResidentInfo...');
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      console.log('Token available:', !!token);
      console.log('UserId:', userId);
      if (!token || !userId) {
        console.log('Missing token or userId, returning early');
        return;
      }
      setResidentId(parseInt(userId));
      try {
        console.log('Making fetch request to:', `${API_ENDPOINTS.RESIDENT}/${userId}`);
        const res = await fetch(`${API_ENDPOINTS.RESIDENT}/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Fetch response status:', res.status);
        if (res.ok) {
          const data = await res.json();
          console.log('Fetched resident data:', data);
          console.log('Setting residentPGY to:', data.year);
          setResidentPGY(data.year);
          setResidentName(data.firstName + ' ' + data.lastName);
          setResidentDataLoaded(true);
          console.log('Resident data loaded successfully');
        } else {
          console.error('Failed to fetch resident data:', res.status);
          const errorText = await res.text();
          console.error('Error response:', errorText);
        }
      } catch (err) {
        console.error('Error fetching resident info:', err);
      }
    };
    // Fetch attending users
    const fetchAttendingUsers = async () => {
      console.log('fetchAttendingUsers called');
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${API_ENDPOINTS.USERS}`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!res.ok) {
          console.error('Failed to fetch users:', res.status);
          return;
        }
        const users = await res.json();
        const attendings = users.filter(u => u.role === 'attending')
          .map(u => `${u.firstName} ${u.lastName}`);
        console.log('Found attendings:', attendings);
        setAttendingUsers(attendings);
      } catch (error) {
        console.error('Error fetching attending users:', error);
      }
    };
    console.log('Calling fetchRotationSchedules...');
    fetchRotationSchedules();
    console.log('Calling fetchResidentInfo...');
    fetchResidentInfo();
    console.log('Calling fetchAttendingUsers...');
    fetchAttendingUsers();
  }, []);

  const fetchCaseLogs = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found");
        return;
      }

      const res = await fetch(`${API_ENDPOINTS.RESIDENT}/case-logs`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
      }

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Response was not JSON");
      }

      const data = await res.json();
      setLogs(data);
    } catch (error) {
      console.error("Error fetching case logs:", error);
      alert(`Error fetching case logs: ${error.message}`);
      setLogs([]);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingLog(null);
  };

  const handleModalSubmit = () => {
    fetchCaseLogs();
    setShowModal(false);
    setEditingLog(null);
  };

  const handleEdit = (logId) => {
    const logToEdit = logs.find(log => log.id === logId);
    if (logToEdit) {
      setEditingLog(logToEdit);
      setShowModal(true);
    }
  };

  const handleDelete = async (logId) => {
    console.log('Delete button clicked for log ID:', logId);
    
    if (!window.confirm("Are you sure you want to delete this case log?")) {
      console.log('Delete operation cancelled by user');
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found in localStorage");
        alert("Authentication error. Please log in again.");
        return;
      }

      console.log('Sending DELETE request to:', `${API_ENDPOINTS.RESIDENT}/case-logs/${logId}`);
      
      const res = await fetch(`${API_ENDPOINTS.RESIDENT}/case-logs/${logId}`, {
        method: "DELETE",
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      console.log('Delete response status:', res.status);
      
      if (!res.ok) {
        const errorData = await res.json();
        console.error('Delete error response:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${res.status}`);
      }

      const responseData = await res.json();
      console.log('Delete successful, response:', responseData);
      
      alert("Case log deleted successfully");
      fetchCaseLogs();
    } catch (error) {
      console.error("Error deleting case log:", error);
      alert(`Error deleting case log: ${error.message}`);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <i className="bi bi-arrow-down-up text-muted"></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="bi bi-arrow-up text-primary"></i>
      : <i className="bi bi-arrow-down text-primary"></i>;
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1);
  };

  const filteredLogs = React.useMemo(() => {
    let filtered = logs.filter(log => {
      const matchesSearch = searchTerm === '' || 
        log.procedureName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.attending.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.rotation.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        if (key === 'dateRange') {
          if (!value.start || !value.end) return true;
          const logDate = new Date(log.date);
          return logDate >= new Date(value.start) && logDate <= new Date(value.end);
        }
        return log[key].toString() === value;
      });

      return matchesSearch && matchesFilters;
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle date sorting
        if (sortConfig.key === 'date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        // Handle boolean sorting
        else if (sortConfig.key === 'robotic' || sortConfig.key === 'minimallyInvasive') {
          aValue = aValue ? 1 : 0;
          bValue = bValue ? 1 : 0;
        }
        // Handle string sorting (case insensitive)
        else if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [logs, searchTerm, filters, sortConfig]);

  const paginatedLogs = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredLogs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLogs, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  const handleExport = () => {
    const headers = [
      'Procedure Name',
      'Date',
      'Rotation',
      'Attending',
      'Patient Population',
      'Role',
      'PGY Level',
      'Robotic',
      'Minimally Invasive'
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredLogs.map(log => [
        `"${log.procedureName}"`,
        new Date(log.date).toLocaleDateString(),
        `"${log.rotation}"`,
        `"${log.attending}"`,
        `"${log.patientPopulation}"`,
        `"${log.role}"`,
        log.pgyLevel,
        log.robotic ? 'Yes' : 'No',
        log.minimallyInvasive ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'case_logs.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper to get rotation for a date
  const getRotationForDate = (dateStr) => {
    console.log('getRotationForDate called with:', dateStr);
    console.log('rotationSchedules:', rotationSchedules);
    console.log('residentPGY:', residentPGY);
    console.log('residentId:', residentId);
    
    if (!rotationSchedules || !residentPGY || !residentId || !dateStr) {
      console.log('Early return - missing data:', {
        hasRotationSchedules: !!rotationSchedules,
        hasResidentPGY: !!residentPGY,
        hasResidentId: !!residentId,
        hasDateStr: !!dateStr
      });
      return null;
    }
    
    const pgy = String(residentPGY);
    console.log('PGY string:', pgy);
    
    const scheduleData = rotationSchedules[pgy]?.schedule;
    console.log('scheduleData:', scheduleData);
    
    if (!scheduleData) {
      console.log('No schedule data found for PGY:', pgy);
      return null;
    }
    
    const residentData = scheduleData[residentId];
    console.log('residentData:', residentData);
    
    if (!residentData) {
      console.log('No resident data found for ID:', residentId);
      return null;
    }
    
    // dateStr is YYYY-MM-DD
    const [year, month] = dateStr.split("-");
    const monthStr = `${year}-${month}`;
    console.log('Looking for rotation in month:', monthStr);
    console.log('Available rotations:', residentData.rotations);
    
    const rotation = residentData.rotations?.[monthStr] || null;
    console.log('Found rotation:', rotation);
    return rotation;
  };

  const handleGenerateReport = () => {
    // Filter logs based on export filters
    let filteredData = logs.filter(log => {
      // Date range filter
      if (exportFilters.startDate && exportFilters.endDate) {
        const logDate = new Date(log.date);
        const startDate = new Date(exportFilters.startDate);
        const endDate = new Date(exportFilters.endDate);
        if (logDate < startDate || logDate > endDate) return false;
      }
      
      // PGY level filter
      if (exportFilters.pgyLevel && log.pgyLevel !== parseInt(exportFilters.pgyLevel)) {
        return false;
      }
      
      // Rotation filter
      if (exportFilters.rotation && log.rotation !== exportFilters.rotation) {
        return false;
      }
      
      // Attending filter
      if (exportFilters.attending && log.attending !== exportFilters.attending) {
        return false;
      }
      
      return true;
    });

    // Generate different report types
    let reportData;
    let reportTitle;
    
    switch (exportFilters.reportType) {
      case 'detailed':
        reportData = generateDetailedReport(filteredData);
        reportTitle = 'Detailed Case Log Report';
        break;
      case 'summary':
        reportData = generateSummaryReport(filteredData);
        reportTitle = 'Summary Case Log Report';
        break;
      case 'statistics':
        reportData = generateStatisticsReport(filteredData);
        reportTitle = 'Statistics Case Log Report';
        break;
      default:
        reportData = generateDetailedReport(filteredData);
        reportTitle = 'Case Log Report';
    }

    // Create HTML report for display and printing
    const htmlReport = generateHTMLReport(reportData, reportTitle, exportFilters, filteredData.length);
    
    // Open in new tab
    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlReport);
    newWindow.document.close();
    
    setShowExportModal(false);
  };

  const generateDetailedReport = (data) => {
    const headers = [
      'Procedure Name',
      'Date',
      'Rotation',
      'Attending',
      'Patient Population',
      'Role',
      'PGY Level',
      'Robotic',
      'Minimally Invasive',
      'Institution',
      'Encounter Number'
    ];
    
    const csvContent = [
      headers.join(','),
      ...data.map(log => [
        `"${log.procedureName || ''}"`,
        new Date(log.date).toLocaleDateString(),
        `"${log.rotation || ''}"`,
        `"${log.attending || ''}"`,
        `"${log.patientPopulation || ''}"`,
        `"${log.role || ''}"`,
        log.pgyLevel || '',
        log.robotic ? 'Yes' : 'No',
        log.minimallyInvasive ? 'Yes' : 'No',
        `"${log.institution || ''}"`,
        `"${log.encounterNumber || ''}"`
      ].join(','))
    ].join('\n');

    return csvContent;
  };

  const generateSummaryReport = (data) => {
    // Group by rotation and count procedures
    const summaryByRotation = data.reduce((acc, log) => {
      const rotation = log.rotation || 'Unknown';
      if (!acc[rotation]) {
        acc[rotation] = {
          totalCases: 0,
          roboticCases: 0,
          minimallyInvasiveCases: 0,
          procedures: {}
        };
      }
      acc[rotation].totalCases++;
      if (log.robotic) acc[rotation].roboticCases++;
      if (log.minimallyInvasive) acc[rotation].minimallyInvasiveCases++;
      
      const procedure = log.procedureName || 'Unknown';
      acc[rotation].procedures[procedure] = (acc[rotation].procedures[procedure] || 0) + 1;
      
      return acc;
    }, {});

    const headers = ['Rotation', 'Total Cases', 'Robotic Cases', 'Minimally Invasive Cases', 'Top Procedures'];
    
    const csvContent = [
      headers.join(','),
      ...Object.entries(summaryByRotation).map(([rotation, stats]) => {
        const topProcedures = Object.entries(stats.procedures)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3)
          .map(([proc, count]) => `${proc} (${count})`)
          .join('; ');
        
        return [
          `"${rotation}"`,
          stats.totalCases,
          stats.roboticCases,
          stats.minimallyInvasiveCases,
          `"${topProcedures}"`
        ].join(',');
      })
    ].join('\n');

    return csvContent;
  };

  const generateStatisticsReport = (data) => {
    const totalCases = data.length;
    const roboticCases = data.filter(log => log.robotic).length;
    const minimallyInvasiveCases = data.filter(log => log.minimallyInvasive).length;
    
    // Cases by PGY level
    const casesByPGY = data.reduce((acc, log) => {
      const pgy = log.pgyLevel || 'Unknown';
      acc[pgy] = (acc[pgy] || 0) + 1;
      return acc;
    }, {});
    
    // Cases by month
    const casesByMonth = data.reduce((acc, log) => {
      const month = new Date(log.date).toISOString().slice(0, 7); // YYYY-MM
      acc[month] = (acc[month] || 0) + 1;
      return acc;
    }, {});
    
    // Top procedures
    const procedureCounts = data.reduce((acc, log) => {
      const procedure = log.procedureName || 'Unknown';
      acc[procedure] = (acc[procedure] || 0) + 1;
      return acc;
    }, {});
    
    const topProcedures = Object.entries(procedureCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10);

    const headers = ['Metric', 'Value'];
    
    const csvContent = [
      headers.join(','),
      'Total Cases,' + totalCases,
      'Robotic Cases,' + roboticCases,
      'Minimally Invasive Cases,' + minimallyInvasiveCases,
      'Robotic Percentage,' + (totalCases > 0 ? ((roboticCases / totalCases) * 100).toFixed(1) : 0) + '%',
      'Minimally Invasive Percentage,' + (totalCases > 0 ? ((minimallyInvasiveCases / totalCases) * 100).toFixed(1) : 0) + '%',
      '',
      'Cases by PGY Level',
      ...Object.entries(casesByPGY).map(([pgy, count]) => `PGY-${pgy},${count}`),
      '',
      'Cases by Month',
      ...Object.entries(casesByMonth).map(([month, count]) => `"${month}",${count}`),
      '',
      'Top 10 Procedures',
      ...topProcedures.map(([procedure, count]) => `"${procedure}",${count}`)
    ].join('\n');

    return csvContent;
  };

  const generateHTMLReport = (reportData, reportTitle, exportFilters, totalCases) => {
    const currentDate = new Date().toLocaleDateString();
    const filtersApplied = [];
    
    if (exportFilters.startDate && exportFilters.endDate) {
      filtersApplied.push(`Date Range: ${exportFilters.startDate} to ${exportFilters.endDate}`);
    }
    if (exportFilters.pgyLevel) {
      filtersApplied.push(`PGY Level: ${exportFilters.pgyLevel}`);
    }
    if (exportFilters.rotation) {
      filtersApplied.push(`Rotation: ${exportFilters.rotation}`);
    }
    if (exportFilters.attending) {
      filtersApplied.push(`Attending: ${exportFilters.attending}`);
    }

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${reportTitle}</title>
        <style>
          @media print {
            .no-print { display: none !important; }
            body { margin: 0; padding: 20px; }
            .page-break { page-break-before: always; }
            .report-container { 
              box-shadow: none !important; 
              padding: 0 !important; 
              margin: 0 !important; 
              max-width: none !important;
              background: white !important;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
          }
          
          .report-container {
            max-width: 1200px;
            margin: 0 auto;
            background: white;
            padding: 30px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            border-radius: 8px;
          }
          
          .report-header {
            text-align: center;
            border-bottom: 3px solid #D3D6DA;
            padding-bottom: 20px;
            margin-bottom: 30px;
            position: relative;
          }
          
          .brand-logo {
            position: absolute;
            top: 0;
            left: 0;
            text-align: left;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .logo-image {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .brand-text {
            display: flex;
            flex-direction: column;
          }
          
          .brand-name {
            font-size: 18px;
            font-weight: bold;
          }
          
          .brand-tagline {
            font-size: 12px;
            color: #666;
            margin: 0;
          }
          
          .report-title {
            font-size: 28px;
            font-weight: bold;
            color: #333;
            margin: 0 0 10px 0;
          }
          
          .report-subtitle {
            font-size: 16px;
            color: #666;
            margin: 0;
          }
          
          .report-meta {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            padding: 15px;
            background-color: #f8f9fa;
            border-radius: 5px;
          }
          
          .meta-section {
            flex: 1;
          }
          
          .meta-title {
            font-weight: bold;
            color: #333;
            margin-bottom: 5px;
          }
          
          .meta-content {
            color: #666;
          }
          
          .filters-applied {
            margin-bottom: 20px;
            padding: 15px;
            background-color: #e3f2fd;
            border-left: 4px solid #2196f3;
            border-radius: 4px;
          }
          
          .filters-title {
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 10px;
          }
          
          .filter-item {
            margin: 5px 0;
            color: #333;
          }
          
          .print-buttons {
            text-align: center;
            margin: 30px 0;
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
          }
          
          .btn {
            padding: 12px 24px;
            margin: 0 10px;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 16px;
            text-decoration: none;
            display: inline-block;
          }
          
          .btn-primary {
            background-color: #007bff;
            color: white;
          }
          
          .btn-secondary {
            background-color: #6c757d;
            color: white;
          }
          
          .btn:hover {
            opacity: 0.8;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 14px;
          }
          
          th, td {
            border: 1px solid #D3D6DA !important;
            padding: 12px;
            text-align: left;
          }
          
          th:nth-child(n+2), td:nth-child(n+2) {
            text-align: center;
          }
          
          th {
            background-color: #f8f9fa;
            font-weight: bold;
          }
          
          tr:nth-child(even) {
            background-color: #f8f9fa;
          }
          
          tr:hover {
            background-color: #e3f2fd;
          }
        </style>
      </head>
      <body>
        <div class="report-container">
          <div class="report-header">
            <div class="brand-logo">
              <div class="logo-image me-2">
                <span><img src="/images/logo3.svg" alt="Medhive Logo" style="width: 48px; height: 48px;"/></span>
              </div>
              <div class="brand-text">
                <div class="brand-name">Medhive</div>
                <div class="brand-tagline">Resident Management System</div>
              </div>
            </div>
            <h1 class="report-title">${reportTitle}</h1>
            <p class="report-subtitle">Generated on ${currentDate}</p>
            <p class="report-subtitle">For ${residentName}</p>
          </div>
          
          <div class="report-meta">
            <div class="meta-section">
              <div class="meta-title">Total Cases</div>
              <div class="meta-content">${totalCases}</div>
            </div>
            <div class="meta-section">
              <div class="meta-title">Report Type</div>
              <div class="meta-content">${reportTitle}</div>
            </div>
            <div class="meta-section">
              <div class="meta-title">Generated By</div>
              <div class="meta-content">Medhive Case Log System</div>
            </div>
          </div>
          
          ${filtersApplied.length > 0 ? `
            <div class="filters-applied">
              <div class="filters-title">Filters Applied:</div>
              ${filtersApplied.map(filter => `<div class="filter-item">• ${filter}</div>`).join('')}
            </div>
          ` : ''}
          
          <div class="print-buttons no-print">
            <button class="btn btn-primary" onclick="window.print()">Print Report</button>
            <button class="btn btn-secondary" onclick="downloadCSV()">Download CSV</button>
            <button class="btn btn-secondary" onclick="window.close()">Close</button>
          </div>
          
          <div class="report-content">
            <table>
              <thead>
                <tr>
                  ${reportData.split('\n')[0].split(',').map(header => `<th>${header.replace(/"/g, '')}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${reportData.split('\n').slice(1).filter(line => line.trim()).map(line => `
                  <tr>
                    ${line.split(',').map(cell => `<td>${cell.replace(/"/g, '')}</td>`).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
        
        <script>
          function downloadCSV() {
            const csvContent = \`${reportData.replace(/`/g, '\\`')}\`;
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', '${reportTitle.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        </script>
      </body>
      </html>
    `;
    
    return html;
  };

  if (loading) {
    return (
      <div className="min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar pageTitle="ACGME Case Logs" />
        <div className="bg-white shadow-sm">
          <Container className="py-2">
            <Row className="align-items-center">
              <Col lg={6} md={6} sm={6} className="col-6">
                <h6 className="mb-0 fw-semibold">My Case Logs</h6>
              </Col>
              <Col lg={6} md={6} sm={6} className="col-6 d-flex justify-content-end">
                <div className="d-flex gap-2">
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-primary" size="sm" id="export-dropdown" className="no-arrow">
                      <div className="d-flex align-items-center">
                        <i className="bi bi-share"></i> <span className=" d-none d-md-block ms-2">Export</span> <i className="bi bi-chevron-down ms-2"></i>
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleExport}>
                        Export to Excel
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => setShowExportModal(true)}>
                        Generate Report
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-plus"></i> <span className=" ms-1 d-none d-md-block">Add Case</span>
                    </div>
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
            <div className="text-muted">Loading case logs...</div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100">
      <PageTitle title="Case Logs" description="Log and manage your surgical cases for ACGME compliance" />
      <Navbar pageTitle="ACGME Case Logs" />

      <div className="bg-white shadow-sm custom-sticky-top">
        <Container className="py-2">
        {/* Header Row */}
        <Row className="align-items-center">
          <Col lg={6} md={6} sm={6} className="col-6">
            <h6 className="mb-0 fw-semibold">My Case Logs</h6>
          </Col>
          <Col lg={6} md={6} sm={6} className="col-6 d-flex justify-content-end">
            <div className="d-flex gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-primary" size="sm" id="export-dropdown" className="no-arrow">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-share"></i> <span className=" d-none d-md-block ms-2">Export</span> <i className="bi bi-chevron-down ms-2"></i>
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleExport}>
                    Export to Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowExportModal(true)}>
                    Generate Report
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
              <div className="d-flex align-items-center">
                <i className="bi bi-plus"></i> <span className=" ms-1 d-none d-md-block">Add Case</span>
              </div>
              </Button>

            </div>
          </Col>
        </Row>
        </Container>
      </div>
        
      <Container className="mt-3">
        {/* Search and Filters */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="p-3 border-0 py-4 d-flex justify-content-between align-items-center">
            {/* Responsive filter: Accordion for sm and below, row/col for md+ */}
            <div className="d-block d-md-none w-100">
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Show Filters</Accordion.Header>
                  <Accordion.Body>
                    <Form.Group>
                      <Row>
                        <Col xs={12} className="mb-2">
                          <Form.Floating>
                            <Form.Control
                              type="text"
                              placeholder="Search by procedure, attending, or rotation..."
                              value={searchTerm}
                              onChange={handleSearch}
                              id="searchInput"
                            />
                            <Form.Label htmlFor="searchInput">Search</Form.Label>
                          </Form.Floating>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <Form.Floating>
                            <Form.Control
                              type="date"
                              value={filters.dateRange.start}
                              onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, start: e.target.value })}
                              id="startDateInput"
                            />
                            <Form.Label htmlFor="startDateInput">Start Date</Form.Label>
                          </Form.Floating>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <Form.Floating>
                            <Form.Control
                              type="date"
                              value={filters.dateRange.end}
                              onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, end: e.target.value })}
                              id="endDateInput"
                            />
                            <Form.Label htmlFor="endDateInput">End Date</Form.Label>
                          </Form.Floating>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <Form.Floating>
                            <Form.Select
                              value={filters.rotation}
                              onChange={(e) => handleFilterChange('rotation', e.target.value)}
                              id="rotationSelect"
                            >
                              <option value="">All Rotations</option>
                              {rotations.map(rotation => (
                                <option key={rotation} value={rotation}>{rotation}</option>
                              ))}
                            </Form.Select>
                            <Form.Label htmlFor="rotationSelect">Rotation</Form.Label>
                          </Form.Floating>
                        </Col>
                        <Col xs={6} className="mb-2">
                          <Form.Floating>
                            <Form.Select
                              value={filters.attending}
                              onChange={(e) => handleFilterChange('attending', e.target.value)}
                              id="attendingSelect"
                            >
                              <option value="">All Attendings</option>
                              {attendings.map(attending => (
                                <option key={attending} value={attending}>{attending}</option>
                              ))}
                            </Form.Select>
                            <Form.Label htmlFor="attendingSelect">Attending</Form.Label>
                          </Form.Floating>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
            <div className="d-none d-md-block w-100">
              <Form.Group>
                <Row>
                  <Col lg={4} md={6} className="mb-2 mb-lg-0 mb-md-2">
                    <Form.Floating>
                      <Form.Control
                        type="text"
                        placeholder="Search by procedure, attending, or rotation..."
                        value={searchTerm}
                        onChange={handleSearch}
                        id="searchInputDesktop"
                      />
                      <Form.Label htmlFor="searchInputDesktop">Search</Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col lg={2} md={3} className="mb-2 mb-lg-0 mb-md-2">
                    <Form.Floating>
                      <Form.Control
                        type="date"
                        value={filters.dateRange.start}
                        onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, start: e.target.value })}
                        id="startDateInputDesktop"
                      />
                      <Form.Label htmlFor="startDateInputDesktop">Start Date</Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col lg={2} md={3} className="mb-2 mb-lg-0 mb-md-0">
                    <Form.Floating>
                      <Form.Control
                        type="date"
                        value={filters.dateRange.end}
                        onChange={(e) => handleFilterChange('dateRange', { ...filters.dateRange, end: e.target.value })}
                        id="endDateInputDesktop"
                      />
                      <Form.Label htmlFor="endDateInputDesktop">End Date</Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col lg={2} md={3} className="mb-2 mb-lg-0 mb-md-0">
                    <Form.Floating>
                      <Form.Select
                        value={filters.rotation}
                        onChange={(e) => handleFilterChange('rotation', e.target.value)}
                        id="rotationSelectDesktop"
                      >
                        <option value="">All Rotations</option>
                        {rotations.map(rotation => (
                          <option key={rotation} value={rotation}>{rotation}</option>
                        ))}
                      </Form.Select>
                      <Form.Label htmlFor="rotationSelectDesktop">Rotation</Form.Label>
                    </Form.Floating>
                  </Col>
                  <Col lg={2} md={3}>
                    <Form.Floating>
                      <Form.Select
                        value={filters.attending}
                        onChange={(e) => handleFilterChange('attending', e.target.value)}
                        id="attendingSelectDesktop"
                      >
                        <option value="">All Attendings</option>
                        {attendings.map(attending => (
                          <option key={attending} value={attending}>{attending}</option>
                        ))}
                      </Form.Select>
                      <Form.Label htmlFor="attendingSelectDesktop">Attending</Form.Label>
                    </Form.Floating>
                  </Col>
                </Row>
              </Form.Group>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover striped className="mb-0">
              <thead>
                <tr>
                  {[
                    { key: 'procedureName', label: 'Procedure',},
                    { key: 'date', label: 'Date' },
                    { key: 'rotation', label: 'Rotation' },
                    { key: 'attending', label: 'Attending' },
                    { key: 'patientPopulation', label: 'Population' },
                    { key: 'role', label: 'Role' },
                    { key: 'pgyLevel', label: 'PGY' },
                    { key: 'robotic', label: 'Robotic' },
                    { key: 'minimallyInvasive', label: 'Min. Invasive' }
                  ].map(({ key, label, }) => (
                    <th 
                      key={key}
                      onClick={() => handleSort(key)}
                      className="cursor-pointer px-3 py-4"
                    >
                      <div className="d-flex align-items-center gap-2">
                        {label}
                        <span className="small">{getSortIcon(key)}</span>
                      </div>
                    </th>
                  ))}
                  <th className="py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-3">{log.procedureName}</td>
                    <td>{new Date(log.date).toLocaleDateString()}</td>
                    <td>{log.rotation}</td>
                    <td>{log.attending}</td>
                    <td>{log.patientPopulation}</td>
                    <td>{log.role}</td>
                    <td>{log.pgyLevel}</td>
                    <td>{log.robotic ? "Yes" : "No"}</td>
                    <td>{log.minimallyInvasive ? "Yes" : "No"}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="light-lite" size="sm" id="dropdown-basic" className="no-arrow">
                          <i className="bi bi-three-dots-vertical"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleViewDetails(log)}>
                            <i className="bi bi-eye me-2"></i>View Details
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleEdit(log.id)}>
                            <i className="bi bi-pencil me-2"></i>Edit
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item onClick={() => handleDelete(log.id)} className="text-danger">
                            <i className="bi bi-trash me-2"></i>Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Enhanced Pagination */}
        {filteredLogs.length > 0 && (
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <span className="text-muted d-none d-lg-block d-md-none d-sm-none">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredLogs.length)} of {filteredLogs.length} case logs
                  </span>
                  <Form.Select
                    key={itemsPerPage}
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                    style={{ width: 'auto' }}
                    size="sm"
                  >
                    <option value={5}>5/page</option>
                    <option value={10}>10/page</option>
                    <option value={25}>25/page</option>
                    <option value={50}>50/page</option>
                  </Form.Select>
                </div>
                
                {totalPages > 1 && (
                  <Pagination size="sm" className="mb-0">

                    <Pagination.Prev 
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    
                    {/* Show page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />

                  </Pagination>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Case Details Modal */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
          <Modal.Header closeButton className="border-bottom-0 px-4">
            <div className="d-flex flex-column justify-content-start align-items-start w-50">
              <h4 className="mb-0">Case Details</h4>
              <div className="text-hl"></div>
            </div>
          </Modal.Header>
          <Modal.Body className="px-4">
            {selectedLog && (
              <div>
                <h4 className="mb-4">{selectedLog.procedureName}</h4>
                <Row className="mt-3">
                  <Col md={6}>
                    <p><strong>Date:</strong> {new Date(selectedLog.date).toLocaleDateString()}</p>
                    <p><strong>Rotation:</strong> {selectedLog ? (getRotationForDate(selectedLog.date) || selectedLog.rotation) : ""}</p>
                    <p><strong>Attending:</strong> {selectedLog.attending}</p>
                    <p><strong>Patient Population:</strong> {selectedLog.patientPopulation}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Role:</strong> {selectedLog.role}</p>
                    <p><strong>PGY Level:</strong> {selectedLog.pgyLevel}</p>
                    <p><strong>Robotic:</strong> {selectedLog.robotic ? "Yes" : "No"}</p>
                    <p><strong>Minimally Invasive:</strong> {selectedLog.minimallyInvasive ? "Yes" : "No"}</p>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top-0 px-4">
            <Button variant="secondary" size="sm" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        <AddCaseLogModal
          isOpen={showModal}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          editingLog={editingLog}
          getRotationForDate={getRotationForDate}
          attendingsFromParent={attendingUsers}
          rotationSchedulesLoaded={rotationSchedulesLoaded}
          residentDataLoaded={residentDataLoaded}
        />

        {/* Export Report Modal */}
        <Modal show={showExportModal} onHide={() => setShowExportModal(false)} size="md">
          <Modal.Header closeButton className="border-bottom-0 px-4">
            <Modal.Title> <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Generate Case Log Report</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
                    <Form.Control
                      type="date"
                      value={exportFilters.startDate}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                    <Form.Control
                      type="date"
                      value={exportFilters.endDate}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="PGY Level" className="mb-3">
                    <Form.Select
                      value={exportFilters.pgyLevel}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, pgyLevel: e.target.value }))}
                    >
                      <option value="">All</option>
                      {(pgyLevelOptions.length > 0 ? pgyLevelOptions : Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `PGY-${i + 1}` }))).map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Rotation" className="mb-3">
                    <Form.Select
                      value={exportFilters.rotation}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, rotation: e.target.value }))}
                    >
                      <option value="">All Rotations</option>
                      {rotations.map(rotation => (
                        <option key={rotation} value={rotation}>{rotation}</option>
                      ))}
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Attending" className="mb-3">
                    <Form.Select
                      value={exportFilters.attending}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, attending: e.target.value }))}
                    >
                      <option value="">All Attendings</option>
                      {attendings.map(attending => (
                        <option key={attending} value={attending}>{attending}</option>
                      ))}
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <FloatingLabel controlId="floatingInput" label="Report Type" className="mb-3">
                    <Form.Select
                      value={exportFilters.reportType}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, reportType: e.target.value }))}
                    >
                      <option value="detailed">Detailed Report</option>
                      <option value="summary">Summary Report</option>
                      <option value="statistics">Statistics Report</option>
                    </Form.Select>
                    </FloatingLabel>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-top-0 px-4">
            <Button variant="light" size="sm" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleGenerateReport}>
              Generate Report
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ResidentCaseLogPage;
